import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select as ChakraSelect,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  Tooltip,
  useColorModeValue,
  Wrap,
  WrapItem,
  Switch,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiSearch,
  FiX,
  FiArrowRight,
  FiArchive,
  FiRotateCcw,
  FiLink,
  FiRepeat,
  FiUsers,
} from 'react-icons/fi';
import { CreatableSelect } from 'chakra-react-select';
import { detectColumnMapping, isFieldConsumed } from '../importMapping';
import { getFieldGroups, fieldLabel } from './studioFields';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

/**
 * The Import Studio Matching Board: your spreadsheet's columns on the left,
 * everything Origin can import on the right. Map by dropdown (left) or
 * click-to-assign (right). Unmatched columns are never lost — they land in
 * the record's Imported Data.
 */
export default function StudioMatchingBoard({
  importType,
  isRetrImport,
  retrState,
  setRetrState,
  csvData,
  csvHeaders,
  columnMapping,
  setColumnMapping,
  restoredMapping,
  crmContactTypes,
  selectedContactTypes,
  setSelectedContactTypes,
  availableTags,
  globalTags,
  setGlobalTags,
  allowAssignmentSelection,
  crmUsers,
  assignToId,
  setAssignToId,
  // Admin-only company-record scope for the whole import
  allowCompanyScope = false,
  importAsCompany = false,
  setImportAsCompany = null,
  companyScopeLocked = false, // candidate type selected — candidates are always company records
  // Contacts-vs-deals detection (null for RETR imports)
  kindDetection,
  activeKind,
  onSwitchKind,
  // Contact Type column value matching
  typeValues = [],
  typeValueMap = {},
  setTypeValueMap,
}) {
  const [fieldSearch, setFieldSearch] = useState('');

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.800');
  const panelHeaderBg = useColorModeValue('gray.50', 'gray.750');
  const matchedBg = useColorModeValue('green.50', 'rgba(72, 187, 120, 0.08)');
  const matchedBorder = useColorModeValue('green.200', 'green.700');
  const unmatchedBg = useColorModeValue('orange.50', 'rgba(237, 137, 54, 0.08)');
  const unmatchedBorder = useColorModeValue('orange.200', 'orange.700');
  const sampleColor = useColorModeValue('gray.500', 'gray.400');
  const openFieldHoverBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.10)');
  const groupTitleColor = useColorModeValue('gray.600', 'gray.400');
  const mappedFieldBg = useColorModeValue('gray.50', 'gray.750');

  const fieldGroups = getFieldGroups(importType, isRetrImport);

  // ------------------------------------------------------------ derived maps
  // columnMapping is field -> header; a header may feed multiple fields.
  const fieldsByHeader = useMemo(() => {
    const map = new Map();
    Object.entries(columnMapping).forEach(([field, header]) => {
      if (!header) return;
      if (!map.has(header)) map.set(header, []);
      map.get(header).push(field);
    });
    return map;
  }, [columnMapping]);

  // First 2 non-empty sample values per column (scan capped for perf)
  const samplesByHeader = useMemo(() => {
    const map = new Map();
    const scanRows = (csvData || []).slice(0, 50);
    (csvHeaders || []).forEach(header => {
      const samples = [];
      for (const row of scanRows) {
        const value = String(row[header] ?? '').trim();
        if (value && !samples.includes(value)) {
          samples.push(value);
          if (samples.length === 2) break;
        }
      }
      map.set(header, samples);
    });
    return map;
  }, [csvData, csvHeaders]);

  const matchedHeaderCount = (csvHeaders || []).filter(h => fieldsByHeader.has(h)).length;
  const unmatchedHeaders = (csvHeaders || []).filter(h => !fieldsByHeader.has(h));

  const allCatalogFields = useMemo(
    () => fieldGroups.flatMap(g => g.fields),
    [fieldGroups]
  );
  const openCatalogFields = allCatalogFields.filter(f => !columnMapping[f.field]);

  // ------------------------------------------------------------------ actions
  const assignField = (field, header) => {
    setColumnMapping({ ...columnMapping, [field]: header });
  };

  const unassignField = (field) => {
    const next = { ...columnMapping };
    delete next[field];
    setColumnMapping(next);
  };

  const unassignHeader = (header) => {
    const next = { ...columnMapping };
    Object.entries(next).forEach(([field, h]) => {
      if (h === header) delete next[field];
    });
    setColumnMapping(next);
  };

  const resetToAutoDetect = () => {
    const rawMapping = detectColumnMapping(csvHeaders, { importType, isRetrImport });
    setColumnMapping(Object.fromEntries(
      Object.entries(rawMapping).filter(([field]) => isFieldConsumed(field, isRetrImport, importType))
    ));
  };

  // ------------------------------------------------------------------ render
  const renderRequirementBadge = (f) => {
    if (f.required) return <Badge colorScheme="red" fontSize="2xs">Required</Badge>;
    if (f.requiredOneOf === 'email-or-phone') return <Badge colorScheme="orange" fontSize="2xs">Email OR Phone</Badge>;
    return null;
  };

  const searchLower = fieldSearch.trim().toLowerCase();
  const fieldMatchesSearch = (f) =>
    !searchLower ||
    fieldLabel(f.field).toLowerCase().includes(searchLower) ||
    f.field.toLowerCase().includes(searchLower) ||
    (f.description || '').toLowerCase().includes(searchLower);

  const unresolvedTypeValues = typeValues.filter(tv => !tv.resolvedTo);
  const typeLabelFor = (value) => (crmContactTypes || []).find(t => t.value === value)?.label || value;

  return (
    <VStack spacing={5} align="stretch">
      {/* Contacts vs deals — decided from the file's columns, not the page
          the importer was opened from. Always switchable. */}
      {!isRetrImport && kindDetection && (
        <Alert
          status={kindDetection.autoSwitched ? 'warning' : 'info'}
          borderRadius="md"
          variant="left-accent"
          alignItems="flex-start"
          data-tour="studio-kind-banner"
        >
          <AlertIcon />
          <Box flex="1" minW={0}>
            {activeKind === 'deals' ? (
              <>
                <Text fontWeight="bold" fontSize="sm">
                  This file looks like a deals list — importing as Deals
                  {kindDetection.autoSwitched ? ' (switched automatically)' : ''}
                </Text>
                <Text fontSize="xs">
                  Deal columns found: {kindDetection.dealHeaders.slice(0, 6).join(', ')}
                  {kindDetection.dealHeaders.length > 6 ? '…' : ''}. Each row creates or updates a deal
                  on that row's contact — contacts that don't exist yet are created automatically.
                </Text>
              </>
            ) : (
              <>
                <Text fontWeight="bold" fontSize="sm">
                  This file looks like a contacts list — importing as Contacts
                  {kindDetection.autoSwitched ? ' (switched automatically)' : ''}
                </Text>
                <Text fontSize="xs">
                  No deal columns (loan number, amount, close date…) were found, so rows import as
                  contacts only and no deals are created.
                </Text>
              </>
            )}
          </Box>
          <Button
            size="xs"
            variant="outline"
            flexShrink={0}
            leftIcon={<FiRepeat />}
            onClick={() => onSwitchKind(activeKind === 'deals' ? 'contacts' : 'deals')}
          >
            {activeKind === 'deals' ? 'Import as contacts only' : 'Import as deals instead'}
          </Button>
        </Alert>
      )}

      {/* Status strip */}
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <HStack spacing={3} flexWrap="wrap">
          <Badge colorScheme="green" px={2} py={1} borderRadius="md">
            <HStack spacing={1}><Icon as={FiCheckCircle} boxSize={3} /><Text>{matchedHeaderCount} matched</Text></HStack>
          </Badge>
          <Badge colorScheme="orange" px={2} py={1} borderRadius="md">
            <HStack spacing={1}><Icon as={FiArchive} boxSize={3} /><Text>{unmatchedHeaders.length} → Imported Data</Text></HStack>
          </Badge>
          {restoredMapping && (
            <Badge colorScheme="purple" px={2} py={1} borderRadius="md">Restored your last mapping for this file format</Badge>
          )}
        </HStack>
        <Button size="xs" variant="ghost" leftIcon={<FiRotateCcw />} onClick={resetToAutoDetect}>
          Reset to auto-detect
        </Button>
      </HStack>

      {/* The two-panel board */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4} alignItems="start">
        {/* LEFT — your spreadsheet */}
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} overflow="hidden" data-tour="studio-board-left">
          <Box px={4} py={3} bg={panelHeaderBg} borderBottomWidth="1px" borderColor={borderColor}>
            <Text fontWeight="bold" fontSize="sm">Your spreadsheet's columns</Text>
            <Text fontSize="xs" color="gray.500">{csvHeaders.length} columns · unmatched ones are saved to each record's "Imported Data"</Text>
          </Box>
          <VStack align="stretch" spacing={2} p={3} maxH="52vh" overflowY="auto">
            {csvHeaders.map(header => {
              const mappedFields = fieldsByHeader.get(header) || [];
              const isMatched = mappedFields.length > 0;
              const samples = samplesByHeader.get(header) || [];

              return (
                <Box
                  key={header}
                  p={3}
                  borderWidth="1px"
                  borderColor={isMatched ? matchedBorder : unmatchedBorder}
                  bg={isMatched ? matchedBg : unmatchedBg}
                  borderRadius="md"
                >
                  <HStack justify="space-between" align="flex-start" spacing={2}>
                    <Box minW={0} flex={1}>
                      <Text fontWeight="semibold" fontSize="sm" isTruncated title={header}>{header}</Text>
                      <Text fontSize="xs" color={sampleColor} isTruncated>
                        {samples.length > 0 ? `e.g. ${samples.join('  ·  ')}` : 'no values in the first rows'}
                      </Text>
                    </Box>
                    {isMatched ? (
                      <HStack spacing={1} flexShrink={0} align="center">
                        <Icon as={FiArrowRight} boxSize={3} color="green.500" />
                        <Wrap spacing={1} justify="flex-end">
                          {mappedFields.map(f => (
                            <WrapItem key={f}>
                              <Badge colorScheme="green" fontSize="2xs">{fieldLabel(f)}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                        <Tooltip label="Unmatch — send this column to Imported Data instead" hasArrow>
                          <IconButton
                            aria-label="Unmatch column"
                            icon={<FiX />}
                            size="xs"
                            minW="18px"
                            h="18px"
                            variant="ghost"
                            onClick={() => unassignHeader(header)}
                          />
                        </Tooltip>
                      </HStack>
                    ) : (
                      <Badge colorScheme="orange" fontSize="2xs" flexShrink={0}>
                        <HStack spacing={1}><Icon as={FiArchive} boxSize={2.5} /><Text>Imported Data</Text></HStack>
                      </Badge>
                    )}
                  </HStack>
                  {!isMatched && (
                    <ChakraSelect
                      size="xs"
                      mt={2}
                      value=""
                      onChange={(e) => { if (e.target.value) assignField(e.target.value, header); }}
                    >
                      <option value="">Match to an Origin field instead…</option>
                      {fieldGroups.map(group => {
                        const open = group.fields.filter(f => !columnMapping[f.field]);
                        if (open.length === 0) return null;
                        return (
                          <optgroup key={group.id} label={group.title}>
                            {open.map(f => (
                              <option key={f.field} value={f.field}>{fieldLabel(f.field)}</option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </ChakraSelect>
                  )}
                </Box>
              );
            })}
          </VStack>
        </Box>

        {/* RIGHT — Origin's catalog */}
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={panelBg} overflow="hidden" data-tour="studio-board-right">
          <Box px={4} py={3} bg={panelHeaderBg} borderBottomWidth="1px" borderColor={borderColor}>
            <HStack justify="space-between" mb={2}>
              <Box>
                <Text fontWeight="bold" fontSize="sm">Everything Origin can import</Text>
                <Text fontSize="xs" color="gray.500">{allCatalogFields.length - openCatalogFields.length} of {allCatalogFields.length} fields receiving data</Text>
              </Box>
            </HStack>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
              <Input
                placeholder="Search Origin fields…"
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                borderRadius="md"
                data-tour="studio-board-search"
              />
            </InputGroup>
          </Box>
          <VStack align="stretch" spacing={3} p={3} maxH="52vh" overflowY="auto">
            {fieldGroups.map(group => {
              const visible = group.fields.filter(fieldMatchesSearch);
              if (visible.length === 0) return null;
              return (
                <Box key={group.id}>
                  <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color={groupTitleColor} mb={1.5}>
                    {group.title}
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {visible.map(f => {
                      const mappedHeader = columnMapping[f.field];
                      const isMapped = !!mappedHeader;
                      const row = (
                        <HStack
                          px={2.5}
                          py={1.5}
                          borderRadius="md"
                          justify="space-between"
                          spacing={2}
                          bg={isMapped ? mappedFieldBg : 'transparent'}
                          _hover={!isMapped ? { bg: openFieldHoverBg } : undefined}
                          cursor={!isMapped ? 'pointer' : 'default'}
                          w="100%"
                        >
                          <HStack spacing={2} minW={0}>
                            <Icon
                              as={isMapped ? FiCheckCircle : FiLink}
                              color={isMapped ? 'green.500' : 'gray.400'}
                              boxSize={3.5}
                              flexShrink={0}
                            />
                            <Tooltip label={f.description} hasArrow openDelay={300} isDisabled={!f.description}>
                              <Text fontSize="sm" isTruncated>{fieldLabel(f.field)}</Text>
                            </Tooltip>
                            {renderRequirementBadge(f)}
                          </HStack>
                          {isMapped ? (
                            <HStack spacing={1} flexShrink={0}>
                              <Text fontSize="xs" color="gray.500" maxW="140px" isTruncated title={mappedHeader}>
                                ← {mappedHeader}
                              </Text>
                              <IconButton
                                aria-label={`Unmap ${fieldLabel(f.field)}`}
                                icon={<FiX />}
                                size="xs"
                                minW="18px"
                                h="18px"
                                variant="ghost"
                                onClick={() => unassignField(f.field)}
                              />
                            </HStack>
                          ) : (
                            <Text fontSize="2xs" color="gray.400" flexShrink={0}>click to match</Text>
                          )}
                        </HStack>
                      );

                      if (isMapped) return <Box key={f.field}>{row}</Box>;

                      // Open field: click opens a picker of sheet columns
                      return (
                        <Menu key={f.field} isLazy>
                          <MenuButton as={Box} w="100%">{row}</MenuButton>
                          <MenuList maxH="280px" overflowY="auto" zIndex={20}>
                            {unmatchedHeaders.length > 0 && (
                              <MenuGroup title="Unmatched columns">
                                {unmatchedHeaders.map(h => (
                                  <MenuItem key={h} fontSize="sm" onClick={() => assignField(f.field, h)}>
                                    <VStack align="flex-start" spacing={0}>
                                      <Text>{h}</Text>
                                      {(samplesByHeader.get(h) || []).length > 0 && (
                                        <Text fontSize="xs" color="gray.500">e.g. {(samplesByHeader.get(h) || []).join(' · ')}</Text>
                                      )}
                                    </VStack>
                                  </MenuItem>
                                ))}
                              </MenuGroup>
                            )}
                            {matchedHeaderCount > 0 && (
                              <MenuGroup title="Already matched columns (reuse)">
                                {csvHeaders.filter(h => fieldsByHeader.has(h)).map(h => (
                                  <MenuItem key={h} fontSize="sm" onClick={() => assignField(f.field, h)}>
                                    {h}
                                    <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                                      → {(fieldsByHeader.get(h) || []).map(fieldLabel).join(', ')}
                                    </Text>
                                  </MenuItem>
                                ))}
                              </MenuGroup>
                            )}
                          </MenuList>
                        </Menu>
                      );
                    })}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        </Box>
      </Grid>

      {/* Contact Type value matching — only when the file has a mapped
          Contact Type column. Auto-matched values need nothing; the rest get
          an explicit value -> Origin type mapping so every ROW lands with the
          right type (most databases mix borrowers, agents, vendors...). */}
      {!isRetrImport && columnMapping.contactTypes && typeValues.length > 0 && (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} data-tour="studio-type-matching">
          <HStack justify="space-between" flexWrap="wrap" gap={2} mb={1}>
            <HStack spacing={2}>
              <Icon as={FiUsers} color="blue.400" />
              <Text fontWeight="bold" fontSize="sm">Match your contact types</Text>
            </HStack>
            <Badge colorScheme={unresolvedTypeValues.length > 0 ? 'orange' : 'green'} borderRadius="md">
              {typeValues.length - unresolvedTypeValues.length} of {typeValues.length} values matched automatically
            </Badge>
          </HStack>
          <Text fontSize="xs" color="gray.500" mb={3}>
            Your "{columnMapping.contactTypes}" column sets each row's contact type individually.
            {unresolvedTypeValues.length > 0
              ? ' These values don\'t match any Origin contact type yet — pick what each one means:'
              : ' Every value matches an Origin contact type — nothing to fix here.'}
          </Text>

          {unresolvedTypeValues.length > 0 && (
            <VStack align="stretch" spacing={2} mb={3}>
              {unresolvedTypeValues.map(tv => {
                const key = tv.value.toLowerCase();
                return (
                  <HStack key={key} spacing={3} flexWrap="wrap">
                    <HStack spacing={2} minW="220px">
                      <Badge colorScheme={typeValueMap[key] ? 'green' : 'orange'} fontSize="xs" px={2} py={0.5} borderRadius="md">
                        {tv.value}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">{tv.count.toLocaleString()} row{tv.count === 1 ? '' : 's'}</Text>
                    </HStack>
                    <Icon as={FiArrowRight} boxSize={3.5} color="gray.400" />
                    <ChakraSelect
                      size="sm"
                      maxW="280px"
                      value={typeValueMap[key] || ''}
                      onChange={(e) => {
                        const next = { ...typeValueMap };
                        if (e.target.value) next[key] = e.target.value;
                        else delete next[key];
                        setTypeValueMap(next);
                      }}
                    >
                      <option value="">Don't import — rows fall back to the default type</option>
                      {(crmContactTypes || []).map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </ChakraSelect>
                  </HStack>
                );
              })}
            </VStack>
          )}

          {typeValues.some(tv => tv.resolvedTo) && (
            <Wrap spacing={1.5}>
              {typeValues.filter(tv => tv.resolvedTo).map(tv => (
                <WrapItem key={tv.value.toLowerCase()}>
                  <Badge colorScheme="green" variant="subtle" fontSize="2xs" borderRadius="md">
                    {tv.value} → {typeLabelFor(tv.resolvedTo)}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>
      )}

      {/* Defaults row */}
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4} data-tour="studio-board-defaults">
        <Text fontWeight="bold" fontSize="sm" mb={3}>Import defaults</Text>
        <Grid templateColumns={{ base: '1fr', md: isRetrImport ? 'repeat(3, 1fr)' : allowAssignmentSelection ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)' }} gap={4}>
          {isRetrImport && (
            <FormControl isRequired>
              <FormLabel fontSize="xs" fontWeight="semibold">State this data covers</FormLabel>
              <ChakraSelect size="sm" value={retrState} onChange={(e) => setRetrState(e.target.value)} placeholder="Select state…">
                {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
              </ChakraSelect>
            </FormControl>
          )}

          {allowAssignmentSelection && (
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="semibold">Assign imported records to</FormLabel>
              <ChakraSelect size="sm" value={assignToId} onChange={(e) => setAssignToId(e.target.value)}>
                <option value="">Assign to me</option>
                {crmUsers.map(user => (
                  <option key={user.uid} value={user.uid}>{user.name || user.Name} ({user.email || user.Email})</option>
                ))}
              </ChakraSelect>
            </FormControl>
          )}

          {allowCompanyScope && setImportAsCompany && (
            <FormControl>
              <FormLabel fontSize="xs" fontWeight="semibold">Record scope</FormLabel>
              <HStack spacing={2} h="32px">
                <Switch
                  size="sm"
                  colorScheme="purple"
                  isChecked={importAsCompany || companyScopeLocked}
                  isDisabled={companyScopeLocked}
                  onChange={(e) => setImportAsCompany(e.target.checked)}
                />
                <Text fontSize="sm">Company records</Text>
                {(importAsCompany || companyScopeLocked) && <Badge colorScheme="purple" fontSize="2xs">visible to all CRM admins</Badge>}
              </HStack>
              <Text fontSize="2xs" color="gray.500" mt={1}>
                {companyScopeLocked
                  ? 'Candidate contacts are always imported as company records.'
                  : 'Applied to newly created records only — never changes existing ones.'}
              </Text>
            </FormControl>
          )}

          <FormControl>
            <FormLabel fontSize="xs" fontWeight="semibold">Default contact type</FormLabel>
            <ChakraSelect
              size="sm"
              value=""
              onChange={(e) => {
                if (e.target.value && !selectedContactTypes.includes(e.target.value)) {
                  setSelectedContactTypes([...selectedContactTypes, e.target.value]);
                }
              }}
              placeholder="+ Add contact type…"
            >
              {(crmContactTypes || [])
                .filter(t => !selectedContactTypes.includes(t.value))
                .map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </ChakraSelect>
            {selectedContactTypes.length > 0 && (
              <Wrap mt={2} spacing={1}>
                {selectedContactTypes.map(val => {
                  const tc = (crmContactTypes || []).find(t => t.value === val);
                  return (
                    <WrapItem key={val}>
                      <Badge
                        colorScheme={tc?.color || 'gray'}
                        cursor="pointer"
                        onClick={() => setSelectedContactTypes(selectedContactTypes.filter(v => v !== val))}
                        title="Click to remove"
                      >
                        {tc?.label || val} &times;
                      </Badge>
                    </WrapItem>
                  );
                })}
              </Wrap>
            )}
            <Text fontSize="2xs" color="gray.500" mt={1}>
              {columnMapping.contactTypes
                ? 'Applies only to rows where your mapped Contact Type column is empty. Never changes existing contacts.'
                : importType === 'deals'
                  ? 'For auto-created contacts — defaults to Borrower if empty. Never changes existing contacts.'
                  : 'Applied to new records only — never changes existing contacts.'}
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs" fontWeight="semibold">Tags for every imported record</FormLabel>
            <CreatableSelect
              isMulti
              size="sm"
              value={globalTags}
              onChange={(options) => {
                // Tags travel as a comma-separated string through the import
                // pipeline, so commas can't be part of a tag value.
                const sanitized = (options || [])
                  .map(o => {
                    const value = String(o.value).replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
                    return value ? { value, label: o.__isNew__ ? value : o.label } : null;
                  })
                  .filter(Boolean);
                setGlobalTags(sanitized);
              }}
              options={(availableTags || []).map(tag => ({ value: tag.value, label: tag.label }))}
              placeholder="Pick existing or type to create…"
              formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
              menuPlacement="top"
            />
          </FormControl>
        </Grid>
      </Box>
    </VStack>
  );
}
